// Modified by AI on 06/09/2026. Edit #2.
import { get, merge } from 'lodash';

import React, {
    useContext, useCallback, useEffect,
} from 'react';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';
import { matchPath } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS, THIRTY_MINUTES_MS } from '../constants/queryKeys';
import Spinner from '../modules/common/loader/LoaderComponent';
import SettingsContext from './SettingsContext';
import { getMergedConfig } from '../modules/common/tags-theme/TFTTagItemConfiig';
import * as colors from '../constants/Colors';
import EmployerContext from './EmployerContext';
import EmployerHttpService from '../api/EmployerHttpService';
import PublicPositionHttpService from '../api/PublicPositionHttpService';
import Logger from '../services/logger';
import { colorSchemeNames } from '../constants/StyleConfig';

const colorSchemeDefault = {
    buttons: {
        default: {
            primary: {
                textColor: colors.white,
                backgroundColor: colors.tuscany,
                borderColor: colors.tuscany,
            },
            light: {
                textColor: colors.white,
                backgroundColor: colors.tuscany,
                borderColor: colors.tuscany,
            },
            tertiary: {
                textColor: colors.white,
                backgroundColor: colors.tuscany,
                borderColor: colors.tuscany,
            },
        },
    },
};

// eslint-disable-next-line import/no-mutable-exports
export let appTheme;

export default withRouter((props) => {
    const { systemConfiguration: { colorSchemeSettings } } = useContext(SettingsContext);

    const { employer, employerId } = useContext(EmployerContext);

    const getPositionDataViaPositionApprovalUuid = useCallback(async (approvalPath) => {
        try {
            const positionApprovalUuid = get(approvalPath, 'params.positionApprovalUuid', null);
            const data = await PublicPositionHttpService.getPublicPositionApproval(positionApprovalUuid) || {};
            const positionData = get(data, 'positionData', null);
            return positionData;
        } catch (error) {
            Logger.error(error);
            return null;
        }
    }, []);

    const approvalMatch = matchPath(props.location.pathname, {
        path: '/public/position-approval-v2/:positionApprovalUuid',
        exact: false,
        strict: false,
    });
    const descriptionMatch = matchPath(props.location.pathname, {
        path: '/public/position-description-v2/:positionId',
        exact: false,
        strict: false,
    });
    const publicRouteKey = get(approvalMatch, 'params.positionApprovalUuid', '')
        || get(descriptionMatch, 'params.positionId', '');

    const { data: theme, error: themeError } = useQuery({
        queryKey: QUERY_KEYS.theme(
            publicRouteKey,
            employerId,
            employer?.settings?.colorScheme,
            employer?.settings?.colorSchemeSettings,
            employer?.resellerEmployerId,
            colorSchemeSettings,
        ),
        queryFn: async () => {
            let fetchedPositionData = null;
            if (!employer) {
                if (approvalMatch) {
                    fetchedPositionData = await getPositionDataViaPositionApprovalUuid(approvalMatch);
                }
                if (descriptionMatch) {
                    const positionId = get(descriptionMatch, 'params.positionId', '');
                    fetchedPositionData = await PublicPositionHttpService.getPublicPositionDescription(positionId);
                }
            }
            const currentEmployerId = employer?._id || fetchedPositionData?.employerId;
            const resellerEmployerId = (employer || fetchedPositionData)?.resellerEmployerId;
            let employerColorSchemeSettingsResult = {};
            let employerColorSchemeFileSettings = {};
            let employerColorScheme;
            let colorSchemeName = colorSchemeNames.TALENYA;
            try {
                if (resellerEmployerId && resellerEmployerId !== currentEmployerId) {
                    const resellerEmployer = await EmployerHttpService.getReseller(currentEmployerId);
                    employerColorScheme = resellerEmployer?.settings?.colorScheme;
                    employerColorSchemeSettingsResult = resellerEmployer?.settings?.colorSchemeSettings || {};
                } else {
                    employerColorScheme = (employer?.settings || fetchedPositionData)?.colorScheme;
                    employerColorSchemeSettingsResult = (employer?.settings || fetchedPositionData)?.colorSchemeSettings || {};
                }
                if (employerColorScheme) {
                    // do not change, it doesn't work another way
                    // eslint-disable-next-line global-require, import/no-dynamic-require, prefer-template
                    employerColorSchemeFileSettings = require('../themes/' + employerColorScheme + '.js')?.default || {};
                    if (employerColorScheme === colorSchemeNames.PAYCOR) {
                        colorSchemeName = colorSchemeNames.PAYCOR;
                    }
                }
                const base = merge({}, colorSchemeSettings, employerColorSchemeFileSettings);
                employerColorSchemeSettingsResult = merge(base, employerColorSchemeSettingsResult);
            } catch (error) {
                employerColorSchemeSettingsResult = colorSchemeSettings;
            }

            const buttonsColorScheme = get(employerColorSchemeSettingsResult, 'buttons', colorSchemeDefault.buttons.default);
            const tagsColorScheme = get(employerColorSchemeSettingsResult, 'criteriaTagsStyles', {});
            const linksColorScheme = get(employerColorSchemeSettingsResult, 'links', {});
            const dashedComponentColorScheme = get(employerColorSchemeSettingsResult, 'dashedComponent', {});
            const insightsColorScheme = get(employerColorSchemeSettingsResult, 'insights', {});
            const thumbButtonsColorScheme = get(employerColorSchemeSettingsResult, 'thumbButtons', {});
            const commonColorScheme = get(employerColorSchemeSettingsResult, 'common', {});
            const talentPoolColorScheme = get(employerColorSchemeSettingsResult, 'talentPool', {});
            const ratingStepColorScheme = get(employerColorSchemeSettingsResult, 'ratingStep', {});
            const loaderColorScheme = get(employerColorSchemeSettingsResult, 'loader', {});
            const squirrelIconsColorScheme = get(employerColorSchemeSettingsResult, 'squirrelIcons', {});
            const highlightColorScheme = get(employerColorSchemeSettingsResult, 'highlight', {});

            const createdTheme = createTheme({
                buttons: buttonsColorScheme,
                tags: getMergedConfig(tagsColorScheme),
                links: linksColorScheme,
                dashedComponent: dashedComponentColorScheme,
                insights: insightsColorScheme,
                thumbButtons: thumbButtonsColorScheme,
                common: commonColorScheme,
                talentPool: talentPoolColorScheme,
                ratingStep: ratingStepColorScheme,
                loader: loaderColorScheme,
                squirrelIcons: squirrelIconsColorScheme,
                highlight: highlightColorScheme,
                colorSchemeName,
            });

            appTheme = createdTheme;
            return createdTheme;
        },
        enabled: !!(employer || !employerId),
        staleTime: Infinity,
        gcTime: THIRTY_MINUTES_MS,
        retry: false,
    });

    useEffect(() => {
        if (themeError) {
            Logger.error('Failed to load theme:', themeError);
        }
    }, [themeError]);

    return (
        <>
            { !theme
                ? (<Spinner/>)
                : (<ThemeProvider theme={theme}>{props.children}</ThemeProvider>)
            }
        </>
    );
});
